// WeightSparkline — Whoop-style inline trend trace. Plots the user's
// weight over the last N days as a single SVG path, with NULL days
// rendered as gaps. The trend is drawn in ink and the goal in violet (the
// plan); the most-recent point gets a small green dot to anchor "you are
// here". Renders inside the hero plate, so every colour comes from a token
// and inverts with it.
//
// No external chart library — single SVG, single path command, easy to
// inspect and tune.

export type SparklinePoint = {
  date: string;
  // null when no weight was logged that day; renders as a gap.
  weight: number | null;
};

export function WeightSparkline({
  points,
  height = 36,
  width = 280,
  goal,
}: {
  points: SparklinePoint[];
  height?: number;
  width?: number;
  // Optional horizontal goal line drawn faintly (e.g. target weight).
  goal?: number;
}) {
  // Domain — Y axis. Use min/max of present points padded by 0.5 kg so the
  // line doesn't kiss the edges. If the user has fewer than 2 logged points
  // the trace is uninteresting; render a placeholder.
  const present = points.filter(
    (p): p is { date: string; weight: number } => p.weight !== null,
  );
  if (present.length < 2) {
    return (
      <div
        className="flex items-center font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint"
        style={{ height }}
      >
        Log a few days to see your trend
      </div>
    );
  }
  const ys = present.map((p) => p.weight);
  let yMin = Math.min(...ys, goal ?? Infinity);
  let yMax = Math.max(...ys, goal ?? -Infinity);
  // Pad the domain so the line + goal line have breathing room.
  yMin -= 0.4;
  yMax += 0.4;
  const xStep = points.length > 1 ? width / (points.length - 1) : 0;
  const project = (i: number, w: number) => ({
    x: i * xStep,
    y: yMax === yMin ? height / 2 : ((yMax - w) / (yMax - yMin)) * height,
  });

  // Build the SVG path. Use M for the first present point, L for subsequent,
  // and break the path with a fresh M after any null gap so we don't draw
  // straight lines across missing days.
  let d = '';
  let inSegment = false;
  points.forEach((p, i) => {
    if (p.weight === null) {
      inSegment = false;
      return;
    }
    const { x, y } = project(i, p.weight);
    d += inSegment ? ` L${x.toFixed(1)} ${y.toFixed(1)}` : ` M${x.toFixed(1)} ${y.toFixed(1)}`;
    inSegment = true;
  });

  // The latest logged point's coordinate (for the green dot).
  const lastIdx = points
    .map((p, i) => (p.weight !== null ? i : -1))
    .reduce((acc, i) => (i > acc ? i : acc), -1);
  const lastP =
    lastIdx >= 0 && points[lastIdx].weight !== null
      ? project(lastIdx, points[lastIdx].weight as number)
      : null;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={`Weight trend over ${points.length} days`}
      className="w-full"
    >
      {goal !== undefined && (
        <line
          x1={0}
          x2={width}
          y1={project(0, goal).y}
          y2={project(0, goal).y}
          stroke="currentColor"
          strokeDasharray="2 3"
          className="text-pop-mid"
        />
      )}
      <path
        d={d.trim()}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        pathLength={1}
        className="text-fg draw-in"
      />
      {lastP && (
        <circle
          cx={lastP.x}
          cy={lastP.y}
          r={2.5}
          fill="currentColor"
          className="text-good-mid"
        />
      )}
    </svg>
  );
}
